import {
  BarChart3,
  CheckCircle2,
  LayoutDashboard,
  Monitor,
  PlugZap,
  ShoppingBag,
  SlidersHorizontal,
  Tv,
  XCircle,
} from "lucide-react";
import { Header } from "../public-landing/components/shared/Header";
import { Footer } from "../public-landing/components/shared/Footer";
import { CookieBanner } from "../public-landing/components/shared/CookieBanner";
import { FacebookPixel } from "../public-landing/components/shared/FacebookPixel";
import { GoogleAnalytics } from "../public-landing/components/shared/GoogleAnalytics";
import {
  LandingConicCtaBg,
  LandingPrimaryImageCtaSection,
} from "../public-landing/components/landing";
import { Button } from "../public-landing/components/ui/button";
import { trackShopifyAppStoreLead } from "../public-landing/lib/facebookPixel";
import { siteConfig } from "../public-landing/siteConfig";
import "../styles/public-reset.css";
import "../styles/public-landing.css";

const PAGE_URL = `${siteConfig.siteUrl}/alternative-to-geckoboard`;
const PAGE_TITLE =
  "Alternative to Geckoboard for Shopify Wallboards - MagnifyBI";
const PAGE_DESCRIPTION =
  "An impartial comparison of MagnifyBI and Geckoboard for Shopify store owners who want embedded, TV-ready ecommerce dashboards and wallboards.";

export const meta = () => {
  return [
    { title: PAGE_TITLE },
    { name: "description", content: PAGE_DESCRIPTION },
    { name: "robots", content: "index,follow" },
    { property: "og:type", content: "article" },
    { property: "og:site_name", content: "MagnifyBI" },
    { property: "og:title", content: PAGE_TITLE },
    { property: "og:description", content: PAGE_DESCRIPTION },
    { property: "og:url", content: PAGE_URL },
    {
      property: "og:image",
      content: `${siteConfig.siteUrl}${siteConfig.socialBanner}`,
    },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: PAGE_TITLE },
    { name: "twitter:description", content: PAGE_DESCRIPTION },
    {
      name: "twitter:image",
      content: `${siteConfig.siteUrl}${siteConfig.socialBanner}`,
    },
    { tagName: "link", rel: "canonical", href: PAGE_URL },
  ];
};

const comparisonRows = [
  {
    area: "Best fit",
    magnify:
      "Shopify store owners who want sales, orders, and merchandising metrics on wallboards without leaving Shopify.",
    geckoboard:
      "Teams that need a broader KPI dashboard platform across many business systems.",
  },
  {
    area: "Shopify experience",
    magnify:
      "Embedded in Shopify Admin, with Shopify-focused setup, auth, and report choices.",
    geckoboard:
      "Available as a separate app with a Shopify data source, alongside many other integrations.",
  },
  {
    area: "Dashboard scope",
    magnify:
      "Purpose-built wallboards and carousel displays for ecommerce operating rhythms.",
    geckoboard:
      "General-purpose dashboard builder for support, sales, finance, marketing, and operations.",
  },
  {
    area: "Pricing posture",
    magnify:
      "Designed as a lean Shopify-specific option for merchants that do not need a broad BI platform.",
    geckoboard:
      "Public pricing starts at a higher dashboard-platform price point, with add-ons for more usage.",
  },
  {
    area: "Customization",
    magnify:
      "Focused themes, card layouts, public links, and display-friendly carousel flows.",
    geckoboard:
      "More mature cross-team dashboard management features on higher plans.",
  },
];

const useCases = [
  {
    title: "Daily store pulse",
    description:
      "Track sales, order volume, average order value, customers, conversion, and inventory signals where the team can see them.",
    icon: <BarChart3 className="h-8 w-8" />,
  },
  {
    title: "Embedded merchant workflow",
    description:
      "Create and manage display boards from Shopify Admin instead of adopting a separate dashboard workspace.",
    icon: <ShoppingBag className="h-8 w-8" />,
  },
  {
    title: "TV-ready wallboards",
    description:
      "Use public display links and carousel rotations for warehouses, sales rooms, offices, and shared screens.",
    icon: <Tv className="h-8 w-8" />,
  },
];

const verdictItems = [
  {
    title: "Choose MagnifyBI if",
    icon: <CheckCircle2 className="h-5 w-5 text-primary-300" />,
    items: [
      "Your main data source is Shopify.",
      "You want a wallboard tool that feels native to store operations.",
      "You prefer a focused product over a broad multi-source dashboard platform.",
      "You are price-sensitive and do not need advanced enterprise dashboard governance.",
    ],
  },
  {
    title: "Choose Geckoboard if",
    icon: <XCircle className="h-5 w-5 text-slate-300" />,
    items: [
      "You need many non-Shopify integrations in one dashboard product.",
      "Your dashboards span support, sales, finance, and executive reporting.",
      "You need advanced alerting, drilldowns, folders, verification, or audit controls.",
      "Your team already uses Geckoboard across departments.",
    ],
  },
];

export default function AlternativeToGeckoboard() {
  return (
    <div
      id="public-landing"
      className="dark fancy-overlay bg-white text-black antialiased min-h-screen flex flex-col"
    >
      <Header className="mb-4" />

      <main className="flex-1 w-full flex flex-col items-center">
        <LandingPrimaryImageCtaSection
          titleComponent={
            <div className="flex flex-col gap-4">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-200">
                MagnifyBI vs Geckoboard
              </p>
              <h1 className="font-semibold text-4xl md:text-5xl lg:text-6xl leading-tight md:max-w-3xl">
                Geckoboard alternative for Shopify wallboards
              </h1>
            </div>
          }
          description="Geckoboard is a capable multi-source dashboard product. MagnifyBI is narrower by design: it is built around Shopify store data, embedded workflows, public display links, and TV-ready wallboards at a leaner price point."
          imageSrc="/landing/static/images/office-preview.png"
          imageAlt="MagnifyBI Shopify wallboard on a large display"
          imagePosition="right"
          imageShadow="hard"
          textPosition="left"
          variant="primary"
          withBackground={false}
          withBackgroundGlow
          backgroundGlowVariant="primary"
          effectComponent={<LandingConicCtaBg variant="primary" />}
        >
          <Button size="xl" asChild>
            <a
              href={siteConfig.appStoreUrl}
              onClick={trackShopifyAppStoreLead}
            >
              View on Shopify App Store
            </a>
          </Button>
          <Button size="xl" variant="outlinePrimary" asChild>
            <a href="#comparison">Compare features</a>
          </Button>
        </LandingPrimaryImageCtaSection>

        <section className="w-full flex justify-center py-12 lg:py-16">
          <div className="container-wide w-full px-6 grid gap-6 md:grid-cols-3">
            {useCases.map((item) => (
              <article
                key={item.title}
                className="rounded-xl border border-primary-100/20 bg-primary-100/10 p-6 shadow-sm"
              >
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-md border border-primary-100/50 bg-primary-900/70 text-primary-300">
                  {item.icon}
                </div>
                <h2 className="text-xl font-semibold">{item.title}</h2>
                <p className="mt-3 text-sm leading-6 text-gray-200">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="comparison"
          className="relative w-full bg-primary-900/10 py-12 lg:py-16"
        >
          <div className="container-wide mx-auto w-full px-6">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-200">
                Impartial comparison
              </p>
              <h2 className="mt-3 text-2xl font-semibold leading-tight md:text-3xl lg:text-4xl">
                MagnifyBI vs Geckoboard for Shopify wallboards
              </h2>
              <p className="mt-5 text-lg text-gray-200">
                The products overlap around live dashboards, but they are aimed
                at different buying decisions. Geckoboard is broader. MagnifyBI
                is more specialized for Shopify operators who want store data on
                shared screens without extra dashboard-platform complexity.
              </p>
            </div>

            <div className="mt-10 overflow-hidden rounded-xl border border-primary-100/20 bg-black/20 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-[0.8fr_1.1fr_1.1fr]">
                <div className="border-b border-primary-100/20 p-4 text-sm font-semibold text-primary-200 md:border-r">
                  Area
                </div>
                <div className="border-b border-primary-100/20 p-4 text-sm font-semibold text-primary-200 md:border-r">
                  MagnifyBI
                </div>
                <div className="border-b border-primary-100/20 p-4 text-sm font-semibold text-primary-200">
                  Geckoboard
                </div>
                {comparisonRows.map((row) => (
                  <div
                    key={row.area}
                    className="contents text-sm leading-6 text-gray-200"
                  >
                    <div className="border-b border-primary-100/10 p-4 font-semibold text-white md:border-r">
                      {row.area}
                    </div>
                    <div className="border-b border-primary-100/10 p-4 md:border-r">
                      {row.magnify}
                    </div>
                    <div className="border-b border-primary-100/10 p-4">
                      {row.geckoboard}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-4 text-xs leading-5 text-gray-400">
              Pricing context checked May 2026: Geckoboard lists plans from
              $119/month billed annually on its own pricing page, while its
              Shopify App Store listing states from $75/month. Pricing changes;
              merchants should confirm current plan limits before buying.
            </p>
          </div>
        </section>

        <section className="w-full py-12 lg:py-16">
          <div className="container-wide mx-auto grid w-full gap-8 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-200">
                Reviewer notes
              </p>
              <h2 className="mt-3 text-2xl font-semibold leading-tight md:text-3xl lg:text-4xl">
                The trade-off is focus versus breadth
              </h2>
              <p className="mt-5 text-lg text-gray-200">
                If a company wants one dashboard layer for every team and tool,
                Geckoboard has the advantage of breadth. If a Shopify merchant
                wants ecommerce metrics on a screen with minimal setup,
                MagnifyBI has the advantage of context.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {verdictItems.map((group) => (
                <article
                  key={group.title}
                  className="rounded-xl border border-primary-100/20 bg-primary-100/10 p-6"
                >
                  <div className="flex items-center gap-3">
                    {group.icon}
                    <h3 className="text-lg font-semibold">{group.title}</h3>
                  </div>
                  <ul className="mt-5 space-y-3 text-sm leading-6 text-gray-200">
                    {group.items.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary-300" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-primary-900/10 py-12 lg:py-16">
          <div className="container-wide mx-auto grid w-full gap-8 px-6 lg:grid-cols-4">
            {[
              {
                title: "Shopify-native setup",
                description:
                  "Designed for merchants already working inside Shopify Admin.",
                icon: <PlugZap className="h-6 w-6" />,
              },
              {
                title: "Wallboard layouts",
                description:
                  "Cards, display themes, and public links for shared screens.",
                icon: <LayoutDashboard className="h-6 w-6" />,
              },
              {
                title: "Carousel displays",
                description:
                  "Rotate multiple wallboards across office, warehouse, or team screens.",
                icon: <Monitor className="h-6 w-6" />,
              },
              {
                title: "Focused controls",
                description:
                  "Less dashboard-platform overhead when Shopify is the job to be done.",
                icon: <SlidersHorizontal className="h-6 w-6" />,
              },
            ].map((item) => (
              <article key={item.title} className="flex flex-col gap-3">
                <div className="text-primary-300">{item.icon}</div>
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="text-sm leading-6 text-gray-200">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        <LandingPrimaryImageCtaSection
          titleComponent={
            <h2 className="text-2xl md:text-3xl lg:text-4xl leading-tight font-semibold md:max-w-2xl">
              A simpler wallboard path for Shopify stores
            </h2>
          }
          description="If your dashboard requirements start and end with Shopify, MagnifyBI is worth considering before adopting a broader dashboard platform."
          imageSrc="/landing/static/images/photo-1557804506-669a67965ba0.jpg"
          imageAlt="Team reviewing Shopify performance on a shared display"
          imagePosition="left"
          imageShadow="hard"
          textPosition="left"
          withBackground={false}
          withBackgroundGlow
          backgroundGlowVariant="primary"
          variant="primary"
        >
          <Button size="xl" asChild>
            <a
              href={siteConfig.appStoreUrl}
              onClick={trackShopifyAppStoreLead}
            >
              View on Shopify App Store
            </a>
          </Button>
          <Button size="xl" variant="outlinePrimary" asChild>
            <a href="/">Back to homepage</a>
          </Button>
        </LandingPrimaryImageCtaSection>
      </main>

      <Footer className="mt-8" />
      <GoogleAnalytics />
      <FacebookPixel />
      <CookieBanner />
    </div>
  );
}
